import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { AdvancedColorPickerContent } from "components/AdvancedColorPicker/AdvancedColorPicker";

import { Icon, IconButton, Popover } from "@mui/material";

import { Icons, Colors } from "utils/Constants";

function SortOrderHeader({ handleTeamSort, sorted }) {
  return (
    <div
      onClick={handleTeamSort}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleTeamSort()}
      style={{ cursor: "pointer" }}
    >
      Sort Order
      <MDBox
        position="absolute"
        top={-3}
        left="100%"
        color={sorted === "asc" ? "text" : "secondary"}
        opacity={sorted === "asc" ? 1 : 0.5}
      >
        <Icon fontSize="medium">arrow_drop_up</Icon>
      </MDBox>
      <MDBox
        position="absolute"
        top={3}
        left="100%"
        color={sorted === "desc" ? "text" : "secondary"}
        opacity={sorted === "desc" ? 1 : 0.5}
      >
        <Icon fontSize="medium">arrow_drop_down</Icon>
      </MDBox>
    </div>
  );
}

SortOrderHeader.propTypes = {
  handleTeamSort: PropTypes.func.isRequired,
  sorted: PropTypes.string.isRequired,
};

export default function TeamData(
  teamList,
  handleOpenNewModal,
  setModalType,
  editLists,
  setEditLists,
  handleDelete,
  handleTeamSort,
  sorted,
  permission,
  handleColorUpdate
) {
  const [rows, setRows] = useState([]);
  const [colorPopover, setColorPopover] = useState({ open: false, anchorEl: null, teamId: null });
  const mongooseId = "_id";
  const teams = teamList || [];

  const handleEdit = (item) => {
    setModalType("Update");
    setEditLists({ ...editLists, team: item });
    handleOpenNewModal("Team");
  };

  const handleColorClick = (event, teamId) => {
    setColorPopover({ open: true, anchorEl: event.currentTarget, teamId });
  };

  const handleColorClose = () => {
    setColorPopover({ open: false, anchorEl: null, teamId: null });
  };

  const handleColorSelect = (color, teamItem, shouldClose = false) => {
    handleColorUpdate(teamItem[mongooseId], color);
    if (shouldClose) {
      handleColorClose();
    }
  };

  const selectedColorTeam = teams.find((item) => item[mongooseId] === colorPopover.teamId);

  useEffect(() => {
    if (teams.length > 0) {
      const list = teams.map((item) => ({
        teamsWfmName: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item?.teamsWfmName}
          </MDTypography>
        ),
        color: (
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {permission?.update ? (
              <MDBox
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: item?.color || "#D0D5DD",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  },
                }}
                onClick={(event) => handleColorClick(event, item[mongooseId])}
              />
            ) : (
              <MDBox
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: item?.color || "#D0D5DD",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                }}
              />
            )}
          </MDBox>
        ),
        sortOrder: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item?.sortOrder}
          </MDTypography>
        ),
        action: (
          <MDBox>
            {permission?.update && (
              <IconButton
                color="secondary"
                fontSize="medium"
                onClick={() => handleEdit(item)}
                sx={{ cursor: "pointer" }}
              >
                {Icons.EDIT}
              </IconButton>
            )}{" "}
            &nbsp;
            {permission?.delete && (
              <IconButton
                color="secondary"
                fontSize="medium"
                sx={{ cursor: "pointer" }}
                onClick={() => handleDelete("Team", item[mongooseId])}
              >
                {Icons.DELETE}
              </IconButton>
            )}
          </MDBox>
        ),
      }));

      setRows(list);
    } else {
      setRows([]);
    }
  }, [teams, permission]);

  return {
    teamColumns: [
      {
        Header: "Teams WFM Name",
        accessor: "teamsWfmName",
        width: "55%",
        align: "left",
      },
      {
        Header: "Color",
        accessor: "color",
        width: "20%",
        align: "left",
      },
      {
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: () => <SortOrderHeader handleTeamSort={handleTeamSort} sorted={sorted} />,
        accessor: "sortOrder",
        width: "15%",
        align: "left",
      },
      ...(permission?.update || permission?.delete
        ? [{ Header: "Action", accessor: "action", width: "10%", align: "right" }]
        : []),
    ],
    teamRows: rows,
    ColorPopover: (
      <Popover
        open={colorPopover.open}
        anchorEl={colorPopover.anchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            backgroundColor: Colors.WHITE,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            overflow: "visible",
            zIndex: 1500,
          },
        }}
      >
        <MDBox sx={{ p: 2 }}>
          <MDTypography variant="caption" fontWeight="medium" mb={1}>
            Select Color
          </MDTypography>
          <AdvancedColorPickerContent
            colors={Colors.SCOPE_COLORS}
            selectedColor={selectedColorTeam?.color}
            onSelect={(color) => handleColorSelect(color, selectedColorTeam, false)}
            onGridSelect={(color) => handleColorSelect(color, selectedColorTeam, true)}
            size={24}
          />
        </MDBox>
      </Popover>
    ),
  };
}
